import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Lightbulb, Loader2, Stethoscope, FlaskConical, ClipboardList, HelpCircle, CheckCircle2, AlertTriangle } from "lucide-react";

const KINDS = [
  { id: "condition", label: "Medical condition", icon: Stethoscope, hint: "e.g. Sickle cell disease" },
  { id: "medication", label: "Medication", icon: FlaskConical, hint: "e.g. Empagliflozin (SGLT2 inhibitor)" },
  { id: "procedure", label: "Dental procedure", icon: ClipboardList, hint: "e.g. Sinus lift" },
  { id: "other", label: "Other idea", icon: HelpCircle, hint: "Feature or content idea" },
];

function SimilarItems({ matches }) {
  if (!matches.length) return null;
  return (
    <div data-testid="suggest-similar" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-900"><AlertTriangle className="w-3.5 h-3.5" /> Similar items already in DentiCision — check before submitting:</p>
      <ul className="mt-1.5 space-y-0.5">
        {matches.map((m) => <li key={m.name} className="text-sm text-amber-900">• {m.name} <span className="text-xs text-amber-700">({m.kind})</span></li>)}
      </ul>
    </div>
  );
}

export default function Suggest() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ kind: params.get("kind") || "condition", name: params.get("name") || "", details: "", reference: "" });
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (form.name.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(() => {
      api.get("/suggestions/check", { params: { q: form.name } }).then(({ data }) => setMatches(data.matches)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.name]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/suggestions", form);
      setSent(data.message);
      toast.success("Suggestion sent");
      setForm({ kind: form.kind, name: "", details: "", reference: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not send");
    } finally {
      setLoading(false);
    }
  };

  const kind = KINDS.find((k) => k.id === form.kind) || KINDS[0];

  return (
    <div data-testid="suggest-page" className="max-w-2xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><Lightbulb className="w-7 h-7 text-amber-500" /> Suggest an Addition</h1>
      <p className="text-slate-500 mt-1">Missing a condition, medication or procedure? Tell us — it goes directly to the DentiCision clinical team. We reply within 1–3 business days.</p>

      {sent && (
        <div data-testid="suggest-success" className="mt-5 flex gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> {sent}
        </div>
      )}

      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-5 space-y-5">
        <div>
          <Label>What would you like to add?</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {KINDS.map((k) => (
              <button
                type="button" key={k.id} data-testid={`suggest-kind-${k.id}`}
                onClick={() => setForm({ ...form, kind: k.id })}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-colors ${form.kind === k.id ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                <k.icon className="w-5 h-5" /> {k.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="s-name">Name</Label>
          <Input id="s-name" data-testid="suggest-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} className="mt-1.5" placeholder={kind.hint} />
          <SimilarItems matches={matches} />
        </div>
        <div>
          <Label htmlFor="s-details">Clinical details / why it matters</Label>
          <Textarea id="s-details" data-testid="suggest-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={4} className="mt-1.5" placeholder="Dental implications, interactions, which procedures are affected..." />
        </div>
        <div>
          <Label htmlFor="s-ref">Supporting reference (optional)</Label>
          <Input id="s-ref" data-testid="suggest-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" placeholder="Guideline, paper or DOI" />
        </div>
        <Button type="submit" disabled={loading} data-testid="suggest-submit" className="bg-slate-900 hover:bg-slate-800">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send suggestion"}
        </Button>
      </form>
    </div>
  );
}
